import React, { useState } from 'react';
import { Search, BookOpen, Users, Star, TrendingUp, Eye, Edit, Copy, Trash2, BookMarked } from 'lucide-react';
import './CourseListTab.css';

interface Course {
  id: number;
  title: string;
  category: string;
  description: string;
  instructor: string;
  students: number;
  modules: number;
  rating: number;
  completion: number;
  status: 'Active' | 'Inactive';
  updatedAt: string;
}

const CourseListTab: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const courses: Course[] = [
    {
      id: 1,
      title: 'Advanced Leadership Development',
      category: 'Leadership',
      description: 'Master the skills needed to lead high-performing teams in modern organizations.',
      instructor: 'Dr. Sarah Mitchell',
      students: 456,
      modules: 3,
      rating: 4.8,
      completion: 85,
      status: 'Active',
      updatedAt: '2 days ago'
    },
    {
      id: 2,
      title: 'Data Analytics Fundamentals',
      category: 'Technical',
      description: 'Learn to analyze data and make data-driven decisions.',
      instructor: 'Mike Chen',
      students: 423,
      modules: 2,
      rating: 4.6,
      completion: 78,
      status: 'Active',
      updatedAt: '5 days ago'
    },
    {
      id: 3,
      title: 'Project Management Professional',
      category: 'Management',
      description: 'Comprehensive PMP certification preparation course.',
      instructor: 'Emily Rodriguez',
      students: 387,
      modules: 3,
      rating: 4.9,
      completion: 82,
      status: 'Active',
      updatedAt: '1 week ago'
    }
  ];

  const stats = [
    { label: 'Total Courses', value: '156', change: '+12 this month', icon: BookOpen, color: '#FF8811' },
    { label: 'Active Learners', value: '2,847', change: '+234 this month', icon: Users, color: '#00A045' },
    { label: 'Avg. Rating', value: '4.7', change: '+0.2 this month', icon: Star, color: '#FACC15' },
    { label: 'Completion Rate', value: '68%', change: '+5% this month', icon: TrendingUp, color: '#8200DB' }
  ];

  return (
    <>
      <div className="trainer-coursemanagement-stats-grid">
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <div key={index} className="trainer-coursemanagement-stat-card">
              <div className="trainer-coursemanagement-stat-content">
                <div className="trainer-coursemanagement-stat-label">{stat.label}</div>
                <div className="trainer-coursemanagement-stat-value">{stat.value}</div>
                <div className="trainer-coursemanagement-stat-change">{stat.change}</div>
              </div>
              <div className="trainer-coursemanagement-stat-icon" style={{ backgroundColor: stat.color }}>
                <IconComponent size={24} color="white" />
              </div>
            </div>
          );
        })}
      </div>

      <div className="trainer-coursemanagement-filters">
        <div className="trainer-coursemanagement-search-box">
          <Search className="trainer-coursemanagement-search-icon" size={20} />
          <input
            type="text"
            placeholder="Search courses by title, instructor, or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          className="trainer-coursemanagement-filter-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">Categories</option>
          <option value="Leadership">Leadership</option>
          <option value="Technical">Technical</option>
          <option value="Management">Management</option>
        </select>
        <select
          className="trainer-coursemanagement-filter-select"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="">Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      <div className="trainer-coursemanagement-courses-grid">
        {courses.map((course) => (
          <div key={course.id} className="trainer-coursemanagement-course-card">
            <div className="trainer-coursemanagement-course-card-header">
              <div className="trainer-coursemanagement-course-icon-wrapper">
                <BookMarked size={48} strokeWidth={1.5} color="#9ca3af" />
              </div>
              <span className={`trainer-coursemanagement-status-badge trainer-coursemanagement-status-${course.status.toLowerCase()}`}>
                {course.status}
              </span>
            </div>

            <h3 className="trainer-coursemanagement-course-title">{course.title}</h3>

            <span className={`trainer-coursemanagement-category-badge trainer-coursemanagement-category-${course.category.toLowerCase()}`}>
              {course.category}
            </span>

            <p className="trainer-coursemanagement-course-description">{course.description}</p>

            <div className="trainer-coursemanagement-course-instructor">by {course.instructor}</div>

            <div className="trainer-coursemanagement-course-stats">
              <div className="trainer-coursemanagement-stat-item">
                <Users className="trainer-coursemanagement-stat-icon-small" size={16} />
                <span>{course.students}</span>
              </div>
              <div className="trainer-coursemanagement-stat-item">
                <BookOpen className="trainer-coursemanagement-stat-icon-small" size={16} />
                <span>{course.modules} modules</span>
              </div>
            </div>

            <div className="trainer-coursemanagement-course-metrics">
              <div className="trainer-coursemanagement-metric">
                <Star className="trainer-coursemanagement-metric-icon" size={16} fill="#FACC15" color="#FACC15" />
                <span>{course.rating}</span>
              </div>
              <div className="trainer-coursemanagement-metric">
                <TrendingUp className="trainer-coursemanagement-metric-icon" size={16} color="#00A045" />
                <span>{course.completion}%</span>
              </div>
            </div>

            <div className="trainer-coursemanagement-course-actions">
              <button className="trainer-coursemanagement-action-btn trainer-coursemanagement-action-view">
                <Eye size={16} /> View
              </button>
              <button className="trainer-coursemanagement-action-btn trainer-coursemanagement-action-edit">
                <Edit size={16} /> Edit
              </button>
              <button className="trainer-coursemanagement-action-btn trainer-coursemanagement-action-icon-btn">
                <Copy size={16} />
              </button>
              <button className="trainer-coursemanagement-action-btn trainer-coursemanagement-action-icon-btn">
                <Trash2 size={16} />
              </button>
            </div>

            <div className="trainer-coursemanagement-course-updated">Updated {course.updatedAt}</div>
          </div>
        ))}
      </div>
    </>
  );
};

export default CourseListTab;